<template>
  <div>
    <div class="d-flex justify-content-between">
      <div class="d-flex excel-reports">
        <label>{{$t('labels.common.excel_reports')}}</label>
        <multiselect v-model="excel_report" :max-height="200"
                     placeholder="" label="value" track-by="value"
                     :options="invoiceMarginReports" :multiple="false"
                     @select="loadExcelReport"
                     select-label="" deselect-label="" selected-label=""
                     :taggable="true" :close-on-select="true"></multiselect>
      </div>
      <div class="d-flex justify-content-end button-group">
        <img src="@/assets/images/common-icons/icon_clear.png"
             @click="cleanFilters"
             :title="$t('buttons.common.clear_filters')">
        <img src="@/assets/images/common-icons/icon_search.svg"
             @click="search"
             :title="$t('buttons.common.search')">
      </div>
    </div>
    <div class="search-form">
      <div class="search-form__filters">
        <div>
          <b-row>
            <b-col>
              <label>{{$t('labels.common.period')}}</label>
              <datepicker v-model="searchData.range_date" range lang="en"
                          :class="{ required: (!searchData.range_date.length)}"
                          placeholder=""
                          range-separator="/" :first-day-of-week="1"></datepicker>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.buyer')}}</label>
              <multiselect v-model="searchData.buyers" :max-height="200"
                           placeholder="" label="name" track-by="name"
                           :options="customers" :multiple="true"
                           :taggable="true" :close-on-select="false"></multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.fixed_margin.impact')}}</label>
              <multiselect v-model="searchData.impact_margin_types" :max-height="200"
                           placeholder="" label="value" track-by="value"
                           :options="impactTypes" :multiple="true"
                           :taggable="true" :close-on-select="false"></multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.invoices.invoice_type')}}</label>
              <multiselect v-model="searchData.type" :max-height="200"
                           placeholder="" label="value" track-by="value"
                           :options="salesInvoicesTypes" :multiple="true"
                           :taggable="true" :close-on-select="false"></multiselect>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="3">
              <label>{{$t('labels.invoices.invoice_date')}}</label>
              <datepicker v-model="searchData.invoice_date" range lang="en"
                          placeholder=""
                          range-separator="/" :first-day-of-week="1"></datepicker>
            </b-col>
            <b-col cols="3">
              <label>{{$t('labels.invoices.invoice_number')}}</label>
              <input class="form-control" v-model="searchData.invoice_number" @keyup.enter="search"
                     placeholder="">
            </b-col>
            <b-col cols="3" class="checkbox-wrapper">
              <label for="current-changes">{{$t('labels.fixed_margin.current_changes')}}</label>
              <input type="checkbox" id="current-changes" v-model="searchData.current_changes">
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <ag-grid-vue class="ag-theme-balham grid" :style="gridHeight" :gridOptions="invoiceMarginGridOptions"
                           :pinnedBottomRowData="invoiceMarginPinnedRowData"
                           :columnDefs="invoiceMarginColumns" :rowData="invoiceMarginList"
                           :postProcessPopup="postProcessPopup">
              </ag-grid-vue>
            </b-col>
          </b-row>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { AgGridVue } from 'ag-grid-vue';
  import {
    mapGetters,
    mapState
  } from 'vuex';
  import Multiselect from 'vue-multiselect';
  import Datepicker from 'vue2-datepicker';
  import {
    cleanFilters,
    saveFilters,
    setFilters,
    getArrayByProperty,
    setNotifyData,
    checkSavedFilterData,
    getFileNameFromContentDisposition,
    transformDateFromDateRangeToStringFormatByIndex
  } from '@/helpers/commonHelpers';
  import { notificationsRenderer } from '@/helpers/renderHelpers';
  import { saveAs } from 'file-saver';
  import { gridMixins } from '@/mixins';

  export default {
    mixins: [gridMixins],
    components: {
      Multiselect,
      Datepicker,
      'ag-grid-vue': AgGridVue
    },
    data() {
      return {
        excel_report: '',
        searchData: {
          range_date: [],
          buyers: [],
          impact_margin_types: [],
          type: [],
          invoice_date: [],
          invoice_number: '',
          current_changes: false
        }
      }
    },
    methods: {
      getSearchParams() {
        return {
          period_start: transformDateFromDateRangeToStringFormatByIndex(this.searchData.range_date, 0),
          period_end: transformDateFromDateRangeToStringFormatByIndex(this.searchData.range_date, 1),
          invoice_date_between_after: transformDateFromDateRangeToStringFormatByIndex(this.searchData.invoice_date, 0),
          invoice_date_between_before: transformDateFromDateRangeToStringFormatByIndex(this.searchData.invoice_date, 1),
          buyers: getArrayByProperty(this.searchData.buyers, 'id').toString(),
          impact_margin_types: getArrayByProperty(this.searchData.impact_margin_types, 'key').toString(),
          type: getArrayByProperty(this.searchData.type, 'key').toString(),
          invoice_number: this.searchData.invoice_number,
          current_changes: this.searchData.current_changes
        };
      },
      search() {
        if (this.checkRequiredFields) {
          const searchData = this.getSearchParams();
          this.$store.dispatch('accounting/loadInvoiceMarginList', searchData);
          saveFilters('invoiceMargin', this.searchData);
        } else {
          this.$notify(setNotifyData('Enter required fields', '', 'warn'));
        }
      },
      loadExcelReport(report) {
        const params = this.getSearchParams();
        this.$http.get(`accounting/generate_invoice_margin_report/${report.key}/`, {
          responseType: 'blob', params: params
        }).then((response, headers) => {
          const disposition = response.headers.get('content-disposition')
          console.log(response.headers)
          const fileName = getFileNameFromContentDisposition(disposition, report.value);
          console.log(fileName)
          saveAs(response.body, fileName + '.xlsx');
        }).catch(error => notificationsRenderer(error.body, 'error'));
      },
      cleanFilters() {
        cleanFilters(this.searchData);
      },
      clearData() {
        this.$store.commit('accounting/setInvoiceMargin', []);
      }
    },
    beforeMount() {
      this.$store.cache.dispatch('accounting/loadSalesInvoicesTypes', {independent_invoice: 'True'});
      this.$store.cache.dispatch('accounting/loadInvoiceMarginReports');
      this.$store.cache.dispatch('accounting/loadImpactTypes');
      this.$store.dispatch('partners/getCustomers');
      if (checkSavedFilterData('invoiceMargin')) {
        this.searchData = setFilters('invoiceMargin');
        if (this.checkRequiredFields) {
          this.search();
        }
      }
    },
    beforeRouteEnter(to, from, next) {
      next(vm => {
        vm.clearData();
      });
    },
    computed: {
      ...mapState('accounting', ['salesInvoicesTypes', 'invoiceMarginList', 'invoiceMarginColumns',
        'invoiceMarginPinnedRowData', 'invoiceMarginReports', 'impactTypes']),
      ...mapState('partners', ['customers']),
      ...mapGetters('accounting', ['invoiceMarginGridOptions'] ),
      checkRequiredFields() {
        return !!(this.searchData.range_date && this.searchData.range_date.length)
      },
      gridHeight() {
        return {height: 'calc(100vh - 240px)'}
      }
    }
  }
</script>

<style scoped>
  .excel-reports {
    min-width: 200px;
    width: 30%;
  }

  .checkbox-wrapper {
    padding-top: 24px;
  }

  .grid {
    height: 500px;
  }

</style>



// WEBPACK FOOTER //
// src/components/pages/modules/sales/fixed-margin/FixedMargin.vue